import { FileModel } from './file';

export enum UserStatus {
  Offline = 'offline',
  Online = 'online',
  Away = 'away',
  DoNotDisturb = 'dnd',
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  isEnabled: number;
  // Admin-controlled: publish this arranger on picaluna.com.au. 1 = public.
  isPublicProfile?: number;
  phone?: string;
  mobile?: string;
  _avatarFile?: FileModel;
  // Personalised email signature: original photo (private), its placement in the
  // canvas, the rendered composite (public-read), and the branding version the
  // composite was rendered against. See unsupervised-task-01.
  sigPhotoFileId?: number | null;
  sigPhotoTransform?: { x: number; y: number; scale: number } | null;
  sigCompositeFileId?: number | null;
  sigFrameVersion?: number | null;
  _sigPhotoFile?: FileModel;
  _sigCompositeFile?: FileModel;
  serviceProviderId: number | null;
  emailSignature: string | null;
  jobTitle?: string | null;
  bio?: string | null;
  credentials?: string[];
  languages?: string[];
  /**
   * Public pages this profile appears on (#184): 'funeral-directors', 'team',
   * or both. Empty = published nowhere, whatever isPublicProfile says.
   * Role does NOT imply placement — the CEO and GM hold the arranger role
   * because they work in the system.
   */
  placements?: string[];
  // Served areas picked via Google Places on /dashboard/settings (max 5).
  servedAreas?: Array<{
    label: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    lat?: number;
    lng?: number;
    placeId?: string;
  }>;
  role?: string;
  roles?: number[];
  regions?: number[];
  status: UserStatus | null;
  createdAt: Date;
  updatedAt: Date;
  isActivated: number;
  uId?: string;
  // Arrangement ids this user has bookmarked. Rides on the is-authenticated /
  // login user object; BookmarkService seeds its toggle state from here.
  arrangementBookmarks?: number[];
}

export interface Group {
  id: number;
  type: string;
  typeId: number;
  title: string;
  createdAt: Date;
  users: User[];
}

export interface UserMessage {
  data: {
    message: string;
    sender: { id: number; email: string };
    sentAt: string;
    groupId?: number;
    recipients?: { id: number; email: string }[];
  };
  messageId: number;
  readAt: string | null;
  createdAt: string;
}

export interface UserMessageResponse {
  data: UserMessage[];
}

export interface AppSyncMessageResponse {
  data: {
    onNotifyMessageDirect: AppSyncMessage;
  };
}

export interface AppSyncMessage {
  sentAt: string;
  message: string;
  messageId: number;
  recipientId: number;
  recipientEmail: string;
  groupId?: number;
  senderId: number;
  senderEmail: string;
}

export interface AppSyncNotification {
  popup?: boolean;
  notification: string;
  recipientId: number;
  senderId: number;
  asyncHandlerId?: string;
  eventDetails?: string;
}

/**
 * What an inbox row is (mirrors conductor-server NotificationKind).
 *   'release' — a release announcement; `notification` is the intro slide's
 *               Markdown and `releaseId` links to the full notes.
 *   'alert'   — was also shown as a popup; the inbox can reopen it.
 *   null      — a plain (discreet) inbox item, HTML.
 */
export type NotificationKind = 'release' | 'alert' | null;

export interface Notification {
  id: number;
  recipientId: number;
  senderId: number;
  status: number;
  notification: string;
  kind?: NotificationKind;
  releaseId?: string | null;
  /** 'md' — Markdown body (may carry conductor-file: media markers). Absent — HTML (legacy rows, server messages). */
  format?: 'md' | null;
  /** 'fullscreen' — the popup/slideshow fills the browser window. */
  display?: 'fullscreen' | null;
  createdAt: Date;
  updatedAt?: any;
  /** The SENDER (name + avatar on the item). */
  _user: User;
}

export interface RegionRole {
  id: number;
  name: string;
  key: string;
  createdAt: Date;
  updatedAt?: any;
}

export interface UserResponse {
  data: User[];
}

export interface GroupResponse {
  data: Group[];
}

export interface UnreadMessageCountResponse {
  data: UnreadMessageCount;
}

export interface UnreadMessageCount {
  totalCount: number;
  counts: {
    [key: string]: {
      userId: number;
      count: number;
    };
  };
}
